import { Theme } from '@emotion/react';

import { IconArrowDown, IconArrowUp } from 'ui/icons';
import { IconChevronDown, IconChevronUp } from '@tock/icons';
import { IconMigrationUtil } from 'omakase/utils/IconMigrationUtil';
import {
  arrowContainerStyles,
  base,
  primaryChildContainer,
  primaryContainer,
  primaryContainerInner,
  secondaryContainer,
} from './collapseCssDefs';
import {
  buttonCoreBase,
  buttonCoreSize,
  buttonCoreVariant,
  buttonIcon,
  buttonIconOnly,
} from 'omakase/Button/buttonCssDefs';
import { useToggle } from '@tock/hooks';
import React, { FunctionComponent, useState } from 'react';

export interface CollapseProps {
  /**
   * If true bgLighter color else bgDefault color
   */
  hasBackgroundColor?: boolean;
  /**
   * If true starts expanded, else collapsed
   */
  startsExpanded?: boolean;
  /**
   * If true takes full width and no border, else narrow and bordered
   */
  isFullBleed?: boolean;
  /**
   * Callback when expanded or collapsed.
   */
  onExpandCollapse?: (expandedOrCollapsed: 'expanded' | 'collapsed') => void;
  /**
   * Phrase that will be appended to aria labels 'show <label>', 'hide <label>'.
   */
  label: string;
  /**
   *
   */
  'data-testid'?: string;
}

/**
 *
 * @param hasBackgroundColor
 * @param startsExpanded
 * @param isFullBleed
 * @param onExpandCollapse
 * @param label
 * @param children
 *   children[0] is always rendered;
 *   children[1...children.length-1] are rendered in the collapsed section
 */
const Collapse: FunctionComponent<CollapseProps> = ({
  hasBackgroundColor = false,
  startsExpanded = false,
  isFullBleed = false,
  onExpandCollapse,
  children,
  label,
  'data-testid': dataTestId,
  ...rest
}) => {
  const [expanded, toggleExpanded] = useToggle(startsExpanded);
  const wrappedSetExpanded = () => {
    onExpandCollapse?.(!expanded ? 'expanded' : 'collapsed');
    toggleExpanded();
  };
  const size = 'small';
  const variant = 'basic';
  const [headChild, ...tailChildren] = React.Children.toArray(children);

  // something like https://stackoverflow.com/a/58942998 to fix the start open condition
  const [hasSkipped, setHasSkipped] = useState(false);

  return (
    <details
      aria-label={`${expanded ? 'hide' : 'show'} ${label}`}
      open={expanded}
      css={(theme: Theme) => base(theme, hasBackgroundColor, isFullBleed)}
      onToggle={() => (hasSkipped || !startsExpanded ? wrappedSetExpanded() : setHasSkipped(true))}
      data-testid={dataTestId}
      {...rest}
    >
      <summary css={primaryContainer}>
        <div css={primaryContainerInner}>
          <div css={primaryChildContainer}>{headChild}</div>
          {/* TODO: Refactor to use a button component, while still rendering a div */}
          <div
            css={(theme: Theme) => [
              buttonCoreBase(theme, variant),
              buttonCoreSize(theme, size),
              buttonCoreVariant(theme, variant, false),
              buttonIconOnly(theme, size),
              arrowContainerStyles,
            ]}
          >
            <span css={(theme: Theme) => buttonIcon(theme, { size, position: 'only' })}>
              {expanded ? (
                <IconMigrationUtil legacyIcon={<IconArrowUp />} newIcon={<IconChevronUp />} />
              ) : (
                <IconMigrationUtil legacyIcon={<IconArrowDown />} newIcon={<IconChevronDown />} />
              )}
            </span>
          </div>
        </div>
      </summary>
      <div css={secondaryContainer}>{tailChildren}</div>
    </details>
  );
};

export default Collapse;
